'use client';

import React, { useState, useEffect, useCallback } from 'react';
// The /pure entry does not inject Stripe.js on import: it loads only when the payment form opens.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import { TestModeBanner } from '@/components/TestModeBanner';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { X, Check, Package, MapPin, CreditCard, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { event as trackEvent } from '@/utils/analytics';
import { MerchandiseProduct, MerchandiseShippingAddress } from '@/types/merchandise';
import { getSortedCountries, getStatesForCountry, getCountryByCode } from '@/utils/constants/countries';
import { detectCountry } from '@/utils/countryDetection';

// Memoised, called only when the embedded checkout renders (client secret present).
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '');
  }
  return stripePromise;
}

// Development mode detection (matching StripePaymentForm pattern)
const isDevelopmentMode = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.location.hostname === 'localhost' ||
    window.location.port === '3000' ||
    window.location.hostname.includes('dev') ||
    process.env.NODE_ENV === 'development'
  );
};

interface LocalizedPrice {
  amount: number;
  currency: string;
  symbol: string;
  formatted: string;
  isApproximate: boolean;
}

interface MerchandiseCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: MerchandiseProduct & {
    pricing: {
      productPrice: number;
      shippingCost: number;
      total: number;
      formatted: {
        productPrice: string;
        shippingCost: string;
        total: string;
      };
      localized?: {
        productPrice: LocalizedPrice;
        shippingCost: LocalizedPrice;
        total: LocalizedPrice;
        currency: string;
        countryCode: string;
      };
    };
    estimatedDelivery: { min: number; max: number };
  };
  coverImageUrl: string;
  childName: string;
  bookId?: string;
  onSuccess?: (orderId: string) => void;
}

type CheckoutStep = 'address' | 'payment' | 'success';

export const MerchandiseCheckoutModal: React.FC<MerchandiseCheckoutModalProps> = ({
  isOpen,
  onClose,
  product,
  coverImageUrl,
  childName,
  bookId,
  onSuccess,
}) => {
  const [step, setStep] = useState<CheckoutStep>('address');
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isComped, setIsComped] = useState(false);

  // Shipping address form state
  const [address, setAddress] = useState<MerchandiseShippingAddress>({
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'US',
    phone: '',
  });
  const [email, setEmail] = useState('');

  // Country/state management
  const [countries] = useState(() => getSortedCountries());
  const [availableStates, setAvailableStates] = useState<{ code: string; name: string }[]>([]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setStep('address');
      setClientSecret(null);
      setSessionId(null);
      setOrderId(null);
      setError(null);
      setIsComped(false);
    }
  }, [isOpen]);

  // Auto-detect user's country on mount
  useEffect(() => {
    const detectUserCountry = async () => {
      try {
        const detectedCountry = await detectCountry();
        if (detectedCountry && countries.find((c) => c.code === detectedCountry)) {
          setAddress((prev) => ({ ...prev, country: detectedCountry }));
        }
      } catch (err) {
        console.error('Failed to detect country:', err);
      }
    };
    detectUserCountry();
  }, [countries]);

  // Update available states when country changes
  useEffect(() => {
    const states = getStatesForCountry(address.country);
    setAvailableStates(states);

    // Clear state if it's not valid for the new country
    if (address.state && states.length > 0 && !states.find((s) => s.code === address.state)) {
      setAddress((prev) => ({ ...prev, state: '' }));
    }
  }, [address.country, address.state]);

  // Handle address form changes
  const handleAddressChange = (field: keyof MerchandiseShippingAddress, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  // Validate address
  const isAddressValid = () => {
    const stateRequired = availableStates.length > 0;
    return (
      address.firstName.trim() &&
      address.lastName.trim() &&
      address.addressLine1.trim() &&
      address.city.trim() &&
      address.postcode.trim() &&
      address.country.trim() &&
      (!stateRequired || address.state.trim()) &&
      email.trim() &&
      email.includes('@')
    );
  };

  // Get postal code placeholder based on country
  const getPostalCodePlaceholder = () => {
    const country = getCountryByCode(address.country);
    return country?.postalCodeFormat || 'Postal Code';
  };

  // Get phone placeholder based on country
  const getPhonePlaceholder = () => {
    const countryPhonePrefixes: Record<string, string> = {
      US: '+1 (555) 000-0000',
      CA: '+1 (555) 000-0000',
      GB: '+44 7700 000000',
      AU: '+61 400 000 000',
      DE: '+49 170 0000000',
      FR: '+33 6 00 00 00 00',
      SA: '+966 50 000 0000',
      AE: '+971 50 000 0000',
      IN: '+91 98765 43210',
      IT: '+39 320 000 0000',
      ES: '+34 600 000 000',
      JP: '+81 90 0000 0000',
      BR: '+55 11 90000 0000',
      MX: '+52 55 0000 0000',
    };
    return countryPhonePrefixes[address.country] || '+XX XXX XXX XXXX';
  };

  // Create checkout session
  const createCheckoutSession = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Track checkout started
      trackEvent({
        action: 'merchandise_checkout_started',
        category: 'merchandise',
        label: `${product.id}_${childName}`,
        value: product.pricing.total,
      });

      const response = await fetch('/api/products/merchandise/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product.id,
          bookId,
          coverImageUrl,
          childName,
          customerEmail: email,
          shippingAddress: address,
          countryCode: address.country,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || errorData.error || 'Failed to create checkout');
      }

      const data = await response.json();

      // Handle comped merchandise: server already fulfilled, skip Stripe entirely
      if (data.comped) {
        setIsComped(true);
        if (data.orderId) setOrderId(data.orderId);
        setStep('success');
        trackEvent({
          action: 'merchandise_purchase_completed',
          category: 'merchandise',
          label: `${product.id}_${childName}`,
          value: product.pricing.total,
        });
        if (data.orderId && onSuccess) onSuccess(data.orderId);
        return;
      }

      setClientSecret(data.clientSecret);
      setSessionId(data.sessionId);
      setOrderId(data.orderId);
      setStep('payment');
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      toast({
        title: 'Checkout Error',
        description: err instanceof Error ? err.message : 'Failed to start checkout',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment completion
  const handlePaymentComplete = useCallback(() => {
    setStep('success');

    trackEvent({
      action: 'merchandise_purchase_completed',
      category: 'merchandise',
      label: `${product.id}_${childName}`,
      value: product.pricing.total,
    });

    if (orderId && onSuccess) {
      onSuccess(orderId);
    }
  }, [orderId, onSuccess, product.id, childName, product.pricing.total]);

  // Development mode payment handler
  const handleDevModePayment = async () => {
    setIsLoading(true);

    // Simulate payment processing
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsLoading(false);
    handlePaymentComplete();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {isComped && <TestModeBanner />}
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-purple-500" />
            <h2 className="font-semibold text-lg">
              {step === 'success' ? 'Order Confirmed!' : `Get Your ${product.name}`}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Step: Address */}
          {step === 'address' && (
            <div className="p-6">
              {/* Product Summary */}
              <div className="flex gap-4 mb-6 p-4 bg-purple-50 rounded-xl">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-white shadow">
                  <img
                    src={coverImageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                  <div className="text-purple-600 font-bold">
                    {product.pricing.localized?.total.formatted || product.pricing.formatted.total}
                    {product.pricing.localized?.total.isApproximate && (
                      <span className="text-xs text-gray-400 ml-1">~</span>
                    )}
                    <span className="text-xs text-gray-500 font-normal ml-1">
                      (incl. shipping)
                    </span>
                  </div>
                  {product.pricing.localized && product.pricing.localized.currency !== 'USD' && (
                    <p className="text-xs text-gray-400 mt-1">
                      You will be charged in USD at current exchange rates
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Shipping Address */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="h-4 w-4" />
                  <span className="font-medium">Shipping Address</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={address.firstName}
                      onChange={(e) => handleAddressChange('firstName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={address.lastName}
                      onChange={(e) => handleAddressChange('lastName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Address Line 1 *</label>
                  <input
                    type="text"
                    value={address.addressLine1}
                    onChange={(e) => handleAddressChange('addressLine1', e.target.value)}
                    placeholder="Street address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={address.addressLine2}
                    onChange={(e) => handleAddressChange('addressLine2', e.target.value)}
                    placeholder="Apt, suite, unit (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Country - moved to top for early selection */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Country *</label>
                  <select
                    value={address.country}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">City *</label>
                    <input
                      type="text"
                      value={address.city}
                      onChange={(e) => handleAddressChange('city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {address.country === 'US' ? 'State' : 'State/Province'}
                      {availableStates.length > 0 && ' *'}
                    </label>
                    {availableStates.length > 0 ? (
                      <select
                        value={address.state}
                        onChange={(e) => handleAddressChange('state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select {address.country === 'US' ? 'State' : 'Province'}</option>
                        {availableStates.map((state) => (
                          <option key={state.code} value={state.code}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={address.state}
                        onChange={(e) => handleAddressChange('state', e.target.value)}
                        placeholder="State/Province"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {address.country === 'US' ? 'ZIP Code' : 'Postal Code'} *
                    </label>
                    <input
                      type="text"
                      value={address.postcode}
                      onChange={(e) => handleAddressChange('postcode', e.target.value)}
                      placeholder={getPostalCodePlaceholder()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Phone (for delivery)</label>
                    <input
                      type="tel"
                      value={address.phone}
                      onChange={(e) => handleAddressChange('phone', e.target.value)}
                      placeholder={getPhonePlaceholder()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Continue button */}
              <button
                onClick={createCheckoutSession}
                disabled={!isAddressValid() || isLoading}
                className="mt-6 w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 rounded-xl
                         flex items-center justify-center gap-2 transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    <span>Continue to Payment</span>
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-3">
                Delivered in {product.estimatedDelivery.min}-{product.estimatedDelivery.max} business days
              </p>
            </div>
          )}

          {/* Step: Payment */}
          {step === 'payment' && (
            <div className="p-6">
              {isDevelopmentMode() ? (
                /* Development Mode Payment Form */
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 text-yellow-700">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-medium">Development Mode</span>
                    </div>
                    <p className="text-sm text-yellow-600 mt-1">
                      Using simplified payment form for testing
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Information (Test Mode)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Card:</span>
                        <span className="font-mono text-sm">4242 4242 4242 4242</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Exp:</span>
                        <span className="font-mono text-sm">12/25</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">CVC:</span>
                        <span className="font-mono text-sm">123</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleDevModePayment}
                    disabled={isLoading}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 rounded-xl
                             flex items-center justify-center gap-2 transition-colors
                             disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <span>Complete Payment (Test Mode)</span>
                    )}
                  </button>
                </div>
              ) : clientSecret ? (
                /* Production Stripe Embedded Checkout */
                <EmbeddedCheckoutProvider
                  stripe={getStripe()}
                  options={{
                    clientSecret,
                    onComplete: handlePaymentComplete,
                  }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
                </div>
              )}

              <button
                onClick={() => setStep('address')}
                className="mt-4 w-full text-gray-600 hover:text-gray-800 text-sm"
              >
                ← Back to shipping details
              </button>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Order Confirmed!
              </h3>
              <p className="text-gray-600 mb-6">
                Your {product.name} is on its way! You'll receive a confirmation email
                at <span className="font-medium">{email}</span>.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Product</span>
                  <span className="font-medium">{product.name}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Total</span>
                  <span className="font-medium">
                    {product.pricing.localized?.total.formatted || product.pricing.formatted.total}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Estimated Delivery</span>
                  <span className="font-medium">
                    {product.estimatedDelivery.min}-{product.estimatedDelivery.max} business days
                  </span>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 rounded-xl transition-colors"
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MerchandiseCheckoutModal;
